import React from "react";
import { Link } from "react-router-dom";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { SITE_URL } from "@/utils/seo";
import { CheckCircle, ArrowRight, Truck, Star } from "lucide-react";

const SITE = SITE_URL;

const zoeModels = [
  {
    slug: "zoe-v2-single",
    name: "Zoe V2 Single",
    type: "Single",
    price: 20,
    highlight: "Lightest single in our fleet",
    desc: "Ultra-lightweight at under 12 lbs. One-hand fold, large canopy, reclining seat and a compact footprint that glides through Disney Park crowds.",
  },
  {
    slug: "zoe-v1-twin",
    name: "Zoe V1 Twin",
    type: "Double",
    price: 28,
    highlight: "Side-by-side double",
    desc: "Side-by-side seating for two kids. Both seats recline independently. Folds flat and fits through standard doorways — no wrestling required.",
  },
  {
    slug: "zoe-v2-twin",
    name: "Zoe V2 Twin",
    type: "Double",
    price: 30,
    highlight: "Most popular Zoe double",
    desc: "The upgraded twin with improved canopy coverage, smooth-rolling wheels and an easy compact fold. Perfect for Magic Kingdom and EPCOT days.",
  },
];

const faqs = [
  {
    q: "Why do so many Disney families choose Zoe strollers?",
    a: "Zoe strollers are known for being exceptionally lightweight — ideal for Disney parks where you fold and unfold constantly for buses, the Skyliner and Monorail. The V2 Single weighs under 12 lbs, folds in seconds, and fits in overhead bins when flying. Families who want maximum portability and a clean park experience tend to love Zoe.",
  },
  {
    q: "Does the Zoe Twin fit the Disney stroller size requirement?",
    a: "Yes. Both the Zoe V1 Twin and V2 Twin fit within Disney's 31-inch-wide by 52-inch-long stroller size requirement. You can take them into all four Disney parks and use them on stroller parking areas as normal.",
  },
  {
    q: "Can I rent a Zoe stroller for delivery to my Disney resort?",
    a: "Yes. We deliver Zoe strollers to every Walt Disney World resort. Delivery and pickup require an in-person lobby meeting — Disney does not allow third-party vendors to leave items with bell services.",
  },
  {
    q: "How does the Zoe V2 Twin compare to other double strollers for Disney?",
    a: "The Zoe V2 Twin is our lightest double, which makes it easier to fold when boarding Disney transportation. It sits slightly lower than a Baby Jogger double but is noticeably easier to push through crowds. If ultra-lightweight is your priority for a double, Zoe is the top pick. If you prefer a slightly larger frame with more storage, the Baby Jogger GT2 Double is worth comparing.",
  },
  {
    q: "Do Zoe strollers recline for naps at Disney?",
    a: "Yes. The Zoe V2 Single and both Zoe twins have reclining seats suitable for napping. The V2 Twin reclines each seat independently, so one child can nap while the other stays upright.",
  },
  {
    q: "Is delivery free to Disney resorts for Zoe stroller rentals?",
    a: "Yes. Standard delivery is free to all Walt Disney World resorts, Universal Orlando, vacation homes and most Orlando hotels. Delivery is always free — no early-delivery surcharges.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Strollers", item: `${SITE}/strollers` },
    { "@type": "ListItem", position: 3, name: "Zoe Stroller Rental Disney World", item: `${SITE}/zoe-stroller-rental-disney-world` },
  ],
};

export default function ZoeStrollerRental() {
  return (
    <>
      <PageSEO
        title="Zoe Stroller Rental Near Disney World | V2 Single & Twin Double Orlando"
        description="Rent a Zoe V2 Single, Zoe V1 Twin or Zoe V2 Twin stroller near Walt Disney World. Ultra-lightweight, park-ready strollers with free delivery to Disney resorts. From $20/day. Enchanted Gear Rentals."
        canonical={`${SITE}/zoe-stroller-rental-disney-world`}
        structuredData={[breadcrumbSchema, faqSchema]}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "#040A0F" }}>

        {/* Hero */}
        <section className="pt-28 pb-16 text-center" style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}>
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <Link to="/strollers" className="hover:text-[#FFD166]">Strollers</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Zoe Stroller Rental</span>
            </nav>
            <div className="inline-flex items-center gap-2 bg-[#FFD166]/10 border border-[#FFD166]/30 text-[#FFD166] text-sm font-medium px-4 py-1.5 rounded-full mb-5">
              <Star className="w-4 h-4" /> Lightest strollers in our fleet
            </div>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-5">
              Zoe Stroller Rental Near Disney World
            </h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              Zoe strollers are the top choice for Disney park days when weight and portability matter most. We rent the Zoe V2 Single and both Zoe Twin doubles — delivered free to your Walt Disney World resort, vacation home or Orlando hotel.
            </p>
            <p className="mt-3 text-sm text-[#B9C8D5] italic">Not affiliated with The Walt Disney Company.</p>
            <div className="mt-7 flex gap-4 justify-center flex-wrap">
              <Link to="/#trip-setup"><button className="btn-primary" data-testid="zoe-cta-top">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
              <Link to="/strollers"><button className="btn-ghost">Browse All Strollers</button></Link>
            </div>
          </div>
        </section>

        {/* Models */}
        <section className="container py-14">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-3">Zoe stroller models available to rent</h2>
          <p className="text-[#B9C8D5] mb-10 max-w-2xl">Every Zoe stroller is sanitized and inspected before delivery. All models meet Disney's park size requirement (31″ × 52″ maximum).</p>
          <div className="grid md:grid-cols-3 gap-6">
            {zoeModels.map((s) => (
              <Link key={s.slug} to={`/strollers/${s.slug}`} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 p-6 transition-all hover:-translate-y-1">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#B9C8D5] border border-[#FFD166]/20 px-2 py-0.5 rounded-full">{s.type}</div>
                  <span className="text-xs text-[#FFD166] bg-[#FFD166]/10 px-2 py-0.5 rounded-full">{s.highlight}</span>
                </div>
                <h3 className="font-serif text-xl text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors mb-2">{s.name}</h3>
                <p className="text-sm text-[#B9C8D5] mb-4">{s.desc}</p>
                <div className="flex items-center justify-between">
                  <span className="text-[#FFD166] font-semibold">From ${s.price}/day</span>
                  <span className="text-xs text-[#B9C8D5] flex items-center gap-1">View details <ArrowRight className="w-3 h-3" /></span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Why Zoe at Disney */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-6">Why Zoe strollers are popular at Disney World</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
            {[
              { icon: CheckCircle, title: "Lightest strollers for Disney buses", body: "Folding and lifting a stroller onto Disney buses is a part of park life. Zoe's ultralight design — under 12 lbs for the V2 Single — makes this easier than any other stroller we carry." },
              { icon: CheckCircle, title: "Fits Disney's size requirement", body: "All Zoe models fit within Disney's 31\" × 52\" park stroller policy. They clear the measurement at every park entrance without issue." },
              { icon: Truck, title: "Free resort delivery", body: "We deliver to every Walt Disney World resort, Universal Orlando, Kissimmee vacation homes and area hotels. All Disney resort deliveries require an in-person lobby handoff." },
              { icon: Star, title: "Fast, one-handed fold", body: "Zoe strollers fold quickly with one hand — useful when you're managing kids, bags and a stroller at the same time. The V2 Twin also folds flat for compact storage." },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="flex gap-4 rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5">
                <Icon className="w-5 h-5 text-[#FFD166] shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-[#F6F1E4]">{title}</div>
                  <div className="text-sm text-[#B9C8D5] mt-1">{body}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Comparison note */}
        <section className="container py-10">
          <div className="max-w-2xl rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-7">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-4">Zoe vs other brands — which is right for your Disney trip?</h2>
            <p className="text-[#B9C8D5] text-sm mb-4">
              Zoe is the clear winner on weight — it's the lightest stroller we carry, single or double. If you prioritize a featherlight fold for Disney buses and the Skyliner, Zoe is unmatched.
            </p>
            <p className="text-[#B9C8D5] text-sm mb-4">
              If you prefer a slightly sturdier all-terrain ride with more storage space, the Baby Jogger GT2 or BOB are strong alternatives. For a premium smooth-ride urban stroller, the Nuna MIXX or UPPAbaby VISTA V2 are excellent choices.
            </p>
            <p className="text-[#B9C8D5] text-sm">
              We carry all of these brands — check our <Link to="/strollers" className="text-[#FFD166] hover:underline">full stroller catalog</Link> or use our <Link to="/stroller-quiz" className="text-[#FFD166] hover:underline">stroller quiz</Link> for a personalized recommendation.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="container py-10">
          <h2 className="font-serif text-3xl text-[#F6F1E4] mb-8">Zoe stroller rental FAQ</h2>
          <div className="space-y-4 max-w-3xl">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-xl bg-[#0A1825] border border-[#FFD166]/10 p-5">
                <summary className="font-semibold text-[#F6F1E4] cursor-pointer list-none flex justify-between items-center gap-3">
                  {f.q}
                  <span className="text-[#FFD166] text-xl leading-none shrink-0 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[#B9C8D5] text-sm leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Related */}
        <section className="container py-8">
          <h2 className="font-serif text-2xl text-[#F6F1E4] mb-5">Compare other brands</h2>
          <div className="flex flex-wrap gap-3">
            {[
              { label: "Baby Jogger stroller rental Disney", to: "/baby-jogger-stroller-rental-disney-world" },
              { label: "Nuna stroller rental Disney", to: "/nuna-stroller-rental-disney-world" },
              { label: "Disney World stroller rentals", to: "/disney-world-stroller-rentals" },
              { label: "Double stroller rentals Orlando", to: "/double-stroller-rentals-orlando" },
              { label: "Last-minute stroller rental", to: "/last-minute-stroller-rental-disney-world" },
            ].map((l) => (
              <Link key={l.to} to={l.to} className="text-sm text-[#FFD166] border border-[#FFD166]/25 px-4 py-2 rounded-full hover:bg-[#FFD166]/10 transition-colors">{l.label}</Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="container pb-20 text-center">
          <div className="max-w-xl mx-auto rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-8">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-3">Reserve your Zoe stroller for Disney World</h2>
            <p className="text-[#B9C8D5] mb-6">Enter your dates and delivery resort to see availability and lock in your Zoe rental.</p>
            <Link to="/#trip-setup"><button className="btn-primary" data-testid="zoe-cta-bottom">Check Availability <ArrowRight className="inline w-4 h-4 ml-1" /></button></Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
